// src/utils/elementsSuivis.ts — SCE Issues
// Suivre un projet (Lot 12 bis) ou un équipement (Lot 12 ter) : format des
// colonnes, appartenance, bascule, et pourquoi un problème est suivi.
// Pur, testable hors ligne.
//
// Suivre un PN ou un N° de série = être prévenu de chaque NOUVEAU problème
// déclaré dessus (flow 1), et voir ses problèmes actifs dans « Éléments
// suivis » sur l'accueil. Suivi manuel : ni le chef de projet ni personne
// n'est abonné d'office (décisions du 25/09 : 1-A, 2-A, 3-A).
//
// ─── Les colonnes ───
//
// Sur la ligne de chacun dans SCE_UserPrefs, une ligne de texte chacune :
//   ProjetsSuivis     `;PN4334;PN3394;`
//   EquipementsSuivis `;4825-SC;2601-H;`
// Les `;` AUX DEUX BOUTS permettent au flow de retrouver les abonnés par un
// seul filtre `substringof(';4825-S;', …)` sans confondre 4825-S et 4825-SC.
// Vide = rien de suivi.
//
// Comparaison : `cleRapprochement` (casse, espaces) — les identifiants de
// Windchill ne sont pas normalisés. On garde la graphie de la fiche.

import { cleRapprochement, estActif, type Issue } from '@/types/issue.types'

export type GenreSuivi = 'projet' | 'equipement'

export interface ElementsSuivis {
  /** PN, graphie de la fiche projet. */
  projets: readonly string[]
  /** N° de série, graphie de la fiche équipement. */
  equipements: readonly string[]
}

export const AUCUN_ELEMENT_SUIVI: ElementsSuivis = { projets: [], equipements: [] }

const SEPARATEUR = ';'

/** Une ligne de texte SharePoint : 255 caractères, soit une trentaine d'éléments. */
export const LONGUEUR_MAX_SUIVIS = 255

export function lireSuivis(brut: string | null | undefined): string[] {
  const vus = new Set<string>()
  const ids: string[] = []
  for (const morceau of (brut ?? '').split(SEPARATEUR)) {
    const id = morceau.trim()
    const cle = cleRapprochement(id)
    if (!id || vus.has(cle)) continue
    vus.add(cle)
    ids.push(id)
  }
  return ids
}

export function ecrireSuivis(ids: readonly string[]): string {
  return ids.length > 0 ? `${SEPARATEUR}${ids.join(SEPARATEUR)}${SEPARATEUR}` : ''
}

export function suit(ids: readonly string[], id: string): boolean {
  const cle = cleRapprochement(id)
  return !!cle && ids.some((p) => cleRapprochement(p) === cle)
}

/** La liste après « suivre » / « ne plus suivre » cet élément. */
export function suivisApres(actuels: readonly string[], id: string, suivre: boolean): string[] {
  const cle = cleRapprochement(id)
  const autres = actuels.filter((p) => cleRapprochement(p) !== cle)
  return suivre && id.trim() ? [...autres, id.trim()] : autres
}

export function listeDuGenre(elements: ElementsSuivis, genre: GenreSuivi): readonly string[] {
  return genre === 'projet' ? elements.projets : elements.equipements
}

export function nombreSuivis(elements: ElementsSuivis): number {
  return elements.projets.length + elements.equipements.length
}

// ─── Pourquoi un problème est suivi ───

/**
 * Suivi pour lui-même (cloche de la fiche problème), par son équipement ou
 * par son projet. Un problème suivi pour plusieurs raisons n'en montre qu'une,
 * la plus précise : lui-même, puis l'équipement, puis le projet.
 */
export type MotifSuivi =
  | { genre: 'probleme' }
  | { genre: 'equipement'; numeroSerie: string }
  | { genre: 'projet'; pn: string }

/**
 * @param abonne le problème est-il suivi pour lui-même ? (cloche, Lot 12)
 * @returns null si le problème n'est pas suivi. Équipement et projet ne
 *          valent que pour un problème ACTIF : un clos n'a plus rien à dire.
 */
export function motifSuivi(issue: Issue, abonne: boolean, elements: ElementsSuivis): MotifSuivi | null {
  if (abonne) return { genre: 'probleme' }
  if (!estActif(issue)) return null
  if (suit(elements.equipements, issue.numeroSerie)) return { genre: 'equipement', numeroSerie: issue.numeroSerie }
  if (suit(elements.projets, issue.pn)) return { genre: 'projet', pn: issue.pn }
  return null
}
